'use client';

// FileUploadZone - Drag-and-drop file upload component for bot content

import { useCallback, useState, useEffect, useMemo } from 'react';
import { useDropzone, type Accept, type FileRejection } from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Fade from '@mui/material/Fade';
import { Button } from '@boty/ui';
import { useTranslations } from 'next-intl';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { SUPPORTED_IMAGE_TYPES, createPastedImageFile } from './validation';

// Supported file types for bot content (documents + images)
export const ACCEPTED_FILE_TYPES: Accept = {
  // Documents
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/plain': ['.txt'],
  // Images
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

// Maximum file size in bytes
// Documents: 10MB - enforced by dropzone
// Images: 5MB - enforced by validation.ts (validateImage) since dropzone uses single maxSize
// Note: Dropzone accepts images up to 10MB, then validateImage() rejects >5MB with proper error
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

export interface FileUploadZoneProps {
  /** Callback when files are accepted */
  onFilesAccepted: (files: File[]) => void;
  /** Callback when files are rejected */
  onFilesRejected?: (rejections: FileRejection[]) => void;
  /** Whether the upload is disabled */
  disabled?: boolean;
  /** Whether multiple files can be selected */
  multiple?: boolean;
}

/**
 * FileUploadZone - A drag-and-drop zone for uploading documents and images
 *
 * Features:
 * - Drag-and-drop with visual feedback (desktop)
 * - Compact mobile layout with tap-to-upload
 * - Click to browse files
 * - File type validation (PDF, DOCX, XLSX, JPG, PNG, WebP)
 * - File size validation (10MB max for documents, 5MB for images)
 * - RTL support via CSS logical properties
 * - Accessible with keyboard navigation
 * - 48px minimum touch targets for mobile
 *
 * @example
 * ```tsx
 * <FileUploadZone
 *   onFilesAccepted={(files) => uploadFiles(files)}
 *   onFilesRejected={(rejections) => showErrors(rejections)}
 * />
 * ```
 */
// Duration to show the upload animation after files are accepted (ms)
// Short duration - just indicates the upload request is registered
const UPLOAD_ANIMATION_DURATION = 2000;

export function FileUploadZone({
  onFilesAccepted,
  onFilesRejected,
  disabled = false,
  multiple = true,
}: FileUploadZoneProps) {
  const t = useTranslations('creator.fileUpload');
  const tImage = useTranslations('creator.imageUpload');

  // Detect OS for paste hint modifier key
  const modifier = useMemo(() => {
    if (typeof navigator === 'undefined') return 'Ctrl';
    return /mac/i.test(navigator.userAgent) ? 'Cmd' : 'Ctrl';
  }, []);

  // Track local upload animation (shows for 2 seconds after file acceptance)
  const [showUploadAnimation, setShowUploadAnimation] = useState(false);

  // Auto-hide animation after duration (always hides after 2 seconds)
  useEffect(() => {
    if (showUploadAnimation) {
      const timer = setTimeout(() => {
        setShowUploadAnimation(false);
      }, UPLOAD_ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [showUploadAnimation]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        setShowUploadAnimation(true);
        onFilesAccepted(acceptedFiles);
      }
      if (rejectedFiles.length > 0) {
        onFilesRejected?.(rejectedFiles);
      }
    },
    [onFilesAccepted, onFilesRejected]
  );

  // Handle clipboard paste events
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (disabled) return;

      const items = e.clipboardData?.files;
      if (!items || items.length === 0) return;

      // Extract only image files from clipboard
      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const file = items[i];
        if (file && SUPPORTED_IMAGE_TYPES.includes(file.type as typeof SUPPORTED_IMAGE_TYPES[number])) {
          imageFiles.push(createPastedImageFile(file));
        }
      }

      // If no images found in clipboard, let default paste behavior proceed
      if (imageFiles.length === 0) return;

      // Prevent default paste behavior since we're handling images
      e.preventDefault();

      setShowUploadAnimation(true);
      onFilesAccepted(imageFiles);
    },
    [disabled, onFilesAccepted]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    disabled,
    multiple,
    noClick: true, // We use a custom button for clicking
    noKeyboard: false, // Keep keyboard navigation
  });

  // Determine border color based on drag state and upload animation
  const getBorderColor = () => {
    if (isDragReject) return 'error.main';
    if (isDragAccept || isDragActive || showUploadAnimation) return 'primary.main';
    return 'divider';
  };

  // Determine background color based on drag state and upload animation
  const getBackgroundColor = () => {
    if (isDragReject) return 'error.lighter';
    if (isDragAccept || isDragActive || showUploadAnimation) return 'primary.lighter';
    return 'background.paper';
  };

  return (
    <Box
      {...getRootProps()}
      onPaste={handlePaste}
      role="button"
      aria-label={t('dropzone.title')}
      tabIndex={disabled ? -1 : 0}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 1, md: 2 },
        py: { xs: 3, md: 6 },
        px: { xs: 2, md: 4 },
        border: '2px dashed',
        borderColor: getBorderColor(),
        borderRadius: 2,
        backgroundColor: getBackgroundColor(),
        transition: 'all 0.2s ease-in-out',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        minHeight: { xs: 100, md: 180 }, // Ensure adequate touch target area
        overflow: 'hidden', // Keep animation contained
        '&:hover': {
          borderColor: disabled ? 'divider' : 'primary.main',
          backgroundColor: disabled ? 'background.paper' : 'action.hover',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
      onClick={disabled ? undefined : open}
    >
      <input {...getInputProps()} />

      {/* Upload animation overlay */}
      <Fade in={showUploadAnimation}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            backgroundColor: 'primary.lighter',
            borderRadius: 2,
            zIndex: 1,
          }}
        >
          <CloudUploadIcon
            sx={{
              fontSize: { xs: 40, md: 56 },
              color: 'primary.main',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.1)', opacity: 0.8 },
              },
            }}
          />
          <Typography
            variant="body1"
            color="primary.main"
            fontWeight="medium"
          >
            {t('dropzone.uploading')}
          </Typography>
          <LinearProgress
            sx={{
              width: '60%',
              maxWidth: 200,
              borderRadius: 1,
            }}
          />
        </Box>
      </Fade>

      <UploadFileIcon
        sx={{
          fontSize: { xs: 32, md: 48 },
          color: isDragActive ? 'primary.main' : 'text.secondary',
        }}
      />

      {/* Desktop: Full instructional text with drag-drop hint */}
      <Box sx={{ textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
        <Typography
          variant="body1"
          color={isDragActive ? 'primary.main' : 'text.primary'}
          fontWeight="medium"
        >
          {isDragActive ? t('dropzone.dropping') : t('dropzone.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('dropzone.subtitle')}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {t('dropzone.sizeLimit')}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {tImage('pasteHint', { modifier })}
        </Typography>
      </Box>

      {/* Mobile: Compact text only (paste hint omitted - clipboard paste is desktop-only) */}
      <Box sx={{ textAlign: 'center', display: { xs: 'block', md: 'none' } }}>
        <Typography
          variant="body2"
          color="text.primary"
          fontWeight="medium"
        >
          {t('dropzone.titleMobile')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('dropzone.subtitleMobile')}
        </Typography>
      </Box>

      <Button
        variant="outlined"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          open();
        }}
        disabled={disabled}
        aria-label={t('dropzone.browseButton')}
        sx={{
          minHeight: 44, // Minimum touch target
          minWidth: 44,
          px: { xs: 2, md: 3 },
        }}
      >
        {t('dropzone.browseButton')}
      </Button>
    </Box>
  );
}
